import { CollapsibleColorPicker } from '@app/pages/i/components/CollapsibleColorPicker';
import { ProductDetailsSection } from '@app/pages/i/components/ProductDetailsSection';
import { ProductPreview } from '@app/pages/i/components/ProductPreviews';
import { PreviewNavDots } from '@app/pages/i/components/ProductPreviews/PreviewNavDots';
import { SelectedPreviewState } from '@app/pages/i/components/ProductPreviews/types';
import {
  filterPreviews,
  getInitialPreview,
  PRODUCT_PREVIEW_REORDER_LIST,
} from '@app/pages/i/components/ProductPreviews/utils';
import { CurrentPreviewProvider } from '@app/pages/i/hooks/useCurrentPreview';
import { ProductPageProps } from '@app/pages/i/types';
import { useSimilarDesignsContext } from '@app/pages/i/utils/SimilarDesignsContext';
import { InventoryItem } from '@app/types/shop-bff/types';
import { trackGA4AddToCartEvent } from '@app/utils/analytics';
import { SearchUUIDStorageKey } from '@app/utils/searchUUIDStorage';
import { useProductConfiguratorForm } from '@app/utils/useProductConfiguratorForm';
import { Box } from '@redbubble/design-system';
import {
  AnalyticsATCContext,
  AnalyticsPageType,
} from '@redbubble/next-analytics/gtm';
import { usePageProps } from '@redbubble/next-provider';
import { uniqBy } from 'lodash';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useRef } from 'react';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useCurrentInventoryStore } from '../../hooks/useCurrentInventoryItem';
import styles from './PrimaryContentSection.module.scss';

interface PrimaryContentSectionProps {
  setAtcDrawerOpen: (open: boolean) => void;
  setConfettiActive: (active: boolean) => void;
}

export const PrimaryContentSection: React.FC<PrimaryContentSectionProps> = ({
  setAtcDrawerOpen,
  setConfettiActive,
}) => {
  useAnalytics();
  const { inventoryCategorySet, defaultInventoryItems } =
    usePageProps<ProductPageProps>();

  const { currentInventoryItem, setCurrentInventoryItem } =
    useCurrentInventoryStore((state) => state);

  // only applicable for products in productPreviewReorderList
  const reorderPreview = PRODUCT_PREVIEW_REORDER_LIST.includes(
    currentInventoryItem.productTypeId
  );
  const showReorderedPreviews = reorderPreview;

  const { showOffsiteSimilarDesignsSection } = useSimilarDesignsContext();

  const handleSetCurrentInventoryItem = (inventoryItem: InventoryItem) => {
    if (currentInventoryItem.blankItemId === inventoryItem.blankItemId) return;
    setCurrentInventoryItem(inventoryItem);
  };

  const {
    configuratorFields,
    onConfiguratorFormChange,
    showColorConfigSeparately,
  } = useProductConfiguratorForm({
    currentInventoryItem,
    inventoryCategorySet,
    setCurrentInventoryItem: handleSetCurrentInventoryItem,
  });

  const previews = currentInventoryItem?.previewSet?.previews
    ? filterPreviews(currentInventoryItem.previewSet.previews)
    : [];

  const { current: initialPreview } = useRef<SelectedPreviewState>(
    getInitialPreview(previews, showReorderedPreviews)
  );

  const { work } = inventoryCategorySet.defaultItem;
  const artist = work.artist;

  const router = useRouter();
  const firstRender = useRef(true);

  useEffect(() => {
    if (currentInventoryItem?.productPageUrl) {
      // always fire a product view on first render
      if (firstRender.current) {
        firstRender.current = false;
      }

      const currentPageUrl = window.location;
      const productPageUrl = new URL(currentInventoryItem.productPageUrl);

      // Only fire once for each url change
      // this stops an infinite loop of updates
      // due to router being in the dependency array
      if (productPageUrl.pathname === currentPageUrl.pathname) return;

      const { slug } = router.query;

      // Only update URL if on a product page using old BlankItemId based routing
      // this should never happen on a PTUID based URL as each blank will have the same
      // productPageUrl however this is an extra guard
      // this should be removed at the end of the PTUID migration
      if (Array.isArray(slug) && slug.length === 3) {
        router.replace(
          {
            pathname: productPageUrl.pathname,
            search: currentPageUrl.search,
            hash: currentPageUrl.hash,
          },
          undefined,
          { shallow: true }
        );
      }
    }
  }, [currentInventoryItem, router]);

  // TODO: Fix upstream, this is a workaround to fix https://redbubble.atlassian.net/browse/DEP-3623

  const uniquePreviews = useMemo(() => {
    if (!showReorderedPreviews) {
      return uniqBy(previews, 'url');
    }
    const index = previews.findIndex(
      (preview) => preview.previewTypeId === 'supplementary'
    );
    const reorderedArray = [
      previews[index],
      ...previews.slice(0, index),
      ...previews.slice(index + 1),
    ];

    return uniqBy(reorderedArray, 'url');
  }, [previews, showReorderedPreviews]);

  return (
    <Box
      className={[
        styles.primaryContent,
        showOffsiteSimilarDesignsSection ? styles.offsitePrimaryContent : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid="primary-content"
    >
      <CurrentPreviewProvider initialPreview={initialPreview}>
        <Box className={styles.productPreviewsLayout}>
          <Box className={styles.preview}>
            {currentInventoryItem?.previewSet?.previews &&
              currentInventoryItem?.previewSet.previews.length > 0 && (
                <ProductPreview
                  currentInventoryItem={currentInventoryItem}
                  previews={uniquePreviews}
                  artistName={artist?.name}
                  workTitle={work.title}
                  productCategoryName={inventoryCategorySet.categoryName}
                  matureContent={work.matureContent}
                  initialPreview={initialPreview}
                />
              )}
          </Box>
        </Box>

        <Box
          className={
            showColorConfigSeparately
              ? styles.mobilePreviewActionsColorPicker
              : styles.mobilePreviewActions
          }
        >
          <Box className={styles.mobilePreviewActionsGrid}>
            <PreviewNavDots
              previews={uniquePreviews}
              showColorConfigSeparately={showColorConfigSeparately}
            />

            {showColorConfigSeparately && (
              <CollapsibleColorPicker
                currentValues={currentInventoryItem.attributes}
                configuratorFields={configuratorFields}
                onConfiguratorFormChange={onConfiguratorFormChange}
              />
            )}
          </Box>
        </Box>
      </CurrentPreviewProvider>
      <ProductDetailsSection
        currentInventoryItem={currentInventoryItem}
        inventoryCategorySet={inventoryCategorySet}
        availableProducts={defaultInventoryItems}
        onAddToCart={() => {
          trackGA4AddToCartEvent({
            item: currentInventoryItem,
            workId: `${currentInventoryItem.workId}`,
            pageType: AnalyticsPageType.ProductDetailsPage,
            context: AnalyticsATCContext.ProductDetailsPage,
            searchUUIDStorageKey: SearchUUIDStorageKey.LocationPathname,
          });
          setAtcDrawerOpen(true);
          setConfettiActive(true);
        }}
        configuratorFields={configuratorFields}
        showColorConfigSeparately={showColorConfigSeparately}
        onConfiguratorFormChange={onConfiguratorFormChange}
      />
    </Box>
  );
};
